import React from "react";

/**
 * Keeps one broken screen from taking the whole shop with it.
 *
 * React unmounts the entire tree on an uncaught render error, so before this
 * existed any component fault left an empty `<div id="root">` — the blank page
 * a shopper saw with nothing to explain it and no way back. The chrome now
 * survives, the failure is named plainly, and the shop stays navigable.
 *
 * `resetKey` clears the error when the route changes, so walking away from a
 * broken page is enough to recover without a reload.
 *
 * The copy is deliberately calm and in Azerbaijani: this is customer-facing,
 * not a developer console. The technical message is kept, but small and below
 * the fold of the message.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // No error-reporting service is wired up here; a swallowed stack would help
    // nobody debug the next one.
    console.error("[atelier] render error:", error, info?.componentStack);
  }

  componentDidUpdate(previous) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-[560px] flex-col justify-center px-6 py-20 text-center">
        <h1 className="font-serif text-[28px] leading-tight text-[var(--adf-ink)]">
          Bu səhifə açılmadı
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-[var(--adf-gray-2)]">
          Texniki nasazlıq oldu. Səbətiniz və hesabınız toxunulmayıb — bu, yalnız
          bu səhifənin göstərilməsinə aiddir.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="min-h-[44px] w-full border border-[var(--adf-ink)] bg-[var(--adf-ink)] px-8 text-[11px] uppercase tracking-[0.16em] text-[var(--adf-white-warm)] transition hover:bg-[var(--adf-espresso)] sm:w-auto"
          >
            Yenidən cəhd et
          </button>
          <a
            href="/"
            className="min-h-[44px] w-full border border-[var(--adf-gray-4)] px-8 py-3 text-[11px] uppercase tracking-[0.16em] text-[var(--adf-ink)] transition hover:border-[var(--adf-ink)] sm:w-auto"
          >
            Ana səhifə
          </a>
        </div>
        <p className="mt-10 break-words text-[12px] text-[var(--adf-gray-3)]">
          {String(error?.message || error)}
        </p>
      </main>
    );
  }
}
